import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Settings as SettingsIcon,
  Search as SearchIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  Public as PublicIcon,
  Bolt as BoltIcon
} from '@mui/icons-material';
import { useUser } from '../../../contexts/UserContext';
import EventDashboard from './EventDashboard';
import DataSourcesConfig from './DataSourcesConfig';
import EventExplorer from './EventExplorer';
import ScheduledJobs from './ScheduledJobs';
import AISearchConsole from './AISearchConsole';
import EventSearch from './EventSearch';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`event-admin-tabpanel-${index}`}
      aria-labelledby={`event-admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `event-admin-tab-${index}`,
    'aria-controls': `event-admin-tabpanel-${index}`,
  };
}

const EventManagement: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const { currentUser } = useUser();
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!currentUser) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You must be logged in to access the Event Management dashboard.
        </Alert>
      </Container>
    );
  }
  
  // Check if user is admin - add your admin check logic here
  const isAdmin = true; // Replace with actual admin check logic
  
  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You do not have permission to access this area.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper 
        elevation={3}
        sx={{ 
          p: 3, 
          display: 'flex', 
          flexDirection: 'column',
          mb: 4
        }}
      >
        <Typography variant="h5" component="h2" gutterBottom>
          Event Management Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Centralized system for collecting, normalizing, and managing event data from external sources.
        </Typography>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs 
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="event management tabs"
          >
            <Tab
              icon={<DashboardIcon />}
              iconPosition="start"
              label="Dashboard"
              {...a11yProps(0)}
            />
            <Tab
              icon={<StorageIcon />}
              iconPosition="start"
              label="Data Sources"
              {...a11yProps(1)}
            />
            <Tab
              icon={<SearchIcon />}
              iconPosition="start"
              label="Event Explorer"
              {...a11yProps(2)}
            />
            <Tab
              icon={<ScheduleIcon />}
              iconPosition="start"
              label="Scheduled Jobs"
              {...a11yProps(3)}
            />
            <Tab
              icon={<BoltIcon />}
              iconPosition="start"
              label="AI Search Console"
              {...a11yProps(4)}
            />
            <Tab
              icon={<PublicIcon />}
              iconPosition="start"
              label="Eventbrite Search"
              {...a11yProps(5)}
            />
          </Tabs>
        </Box>
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <CircularProgress />
          </Box>
        )}
        
        <TabPanel value={tabValue} index={0}>
          <EventDashboard />
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <DataSourcesConfig />
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          <EventExplorer />
        </TabPanel>
        
        <TabPanel value={tabValue} index={3}>
          <ScheduledJobs />
        </TabPanel>
        
        <TabPanel value={tabValue} index={4}>
          <AISearchConsole />
        </TabPanel>
        
        <TabPanel value={tabValue} index={5}>
          <EventSearch />
        </TabPanel>
      </Paper>
    </Container>
  );
};

export default EventManagement;
